import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import Navbar from "../../../components/Navbar";
import { motion, AnimatePresence } from "framer-motion";
import {
  Download,
  Store,
  Globe,
  CheckCircle2,
  Clock,
  Search,
  ArrowUpDown,
  MoreVertical,
  Tag,
  Users,
  BarChart3,
  Filter,
  ChevronDown,
  Trash2,
  X,
  Key,
  Calendar,
  User,
  Shield,
  Lock,
  Eye,
  Edit3,
  AlertTriangle,
  FileText,
  Settings,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-toastify";
import { DealsRequest } from "../../../requests/vendor/DealsRequest";
import moment from "moment";
import { debounce } from "lodash";
import { createPortal } from "react-dom";
import PermissionDeniedModal from "../../../components/Permissions/PermissionDeniedModal";
import RedemptionDealDetailsSlider from "./RedemptionDealDetailsSlider";

const RedemptionOverview = () => {
  // Permission checks
  const userPermissions = useSelector((state) => state?.user?.userPermissions);
  const isMainVendor = useSelector((state) => state?.user?.isMainVendor);

  // Check specific redemption permissions
  const hasRedeemOffersPermission =
    isMainVendor ||
    (userPermissions &&
      userPermissions["Redeem_Offers"] &&
      userPermissions["Redeem_Offers"].includes("Redeem_Offers"));

  const hasRedeemEditPermission =
    isMainVendor ||
    (userPermissions &&
      userPermissions["Redeem_Offers"] &&
      userPermissions["Redeem_Offers"].includes("Redeem_Edit"));

  const hasRedeemDeletePermission =
    isMainVendor ||
    (userPermissions &&
      userPermissions["Redeem_Offers"] &&
      userPermissions["Redeem_Offers"].includes("Redeem_Delete"));

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedDeal, setSelectedDeal] = useState("all");
  const dropdownRef = useRef(null);
  const [filterSearchTerm, setFilterSearchTerm] = useState("");
  const searchInputRef = useRef(null);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [redemptionData, setRedemptionData] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDealDetailsSliderOpen, setIsDealDetailsSliderOpen] = useState(false);
  const [selectedRedemptionForDetails, setSelectedRedemptionForDetails] = useState(null);

  // Permission-aware state
  const [selectedItems, setSelectedItems] = useState([]);
  const [isBulkActionMode, setIsBulkActionMode] = useState(false);
  const [showPermissionTooltip, setShowPermissionTooltip] = useState(false);

  //* Statistics
  const [totalRedemptions, setTotalRedemptions] = useState(0);
  const [totalPendingRedemptions, setTotalPendingRedemptions] = useState(0);
  const [totalCompletedRedemptions, setTotalCompletedRedemptions] = useState(0);
  const [totalRedemptionCompletionRate, setTotalRedemptionCompletionRate] =
    useState(0);
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Move fetchRedemptions to its own function outside useEffect
  const fetchRedemptions = async () => {
    setIsLoading(true);
    try {
      const response = await DealsRequest.fetchRedemptions();
      if (response?.data?.status_code === "000" && !response?.data?.in_error) {
        // Newer API wraps the list: data.data = { data: [], pagination }
        // Older API returned data.data as the array itself.
        const payload = response?.data?.data;
        const rawData = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : [];

        // Transform data to flatten deal_summary properties for easier access
        const transformedData = rawData.map(item => ({
          // Redemption info
          redemption_id: item.id,
          user_id: item.user_id,
          deal_id: item.deal_id,
          redemption_status: item.status,
          user_deal_slug: item.user_deal_slug,
          redemption_code: item.redemption_code,
          customer_name: item.user?.full_name || "N/A",
          updated_at: item.updated_at,
          created_at: item.created_at,
          // Quantity customer requested when redeeming (root level)
          quantity: item.quantity != null ? String(item.quantity) : null,
          // Deal quantity left (from deal_summary). null/undefined/""/"unlimited" = unlimited (per NewDealForm QuantitySection)
          deal_quantity_left: (() => {
            const raw = item.deal_summary?.quantity;
            if (raw == null || raw === "") return null;
            const s = String(raw).trim().toLowerCase();
            if (s === "unlimited" || s === "∞") return null;
            return String(raw);
          })(),

          // Deal summary - flattened for easy access
          deal_title: item.deal_summary?.title || "N/A",
          redemption_type: item.deal_summary?.redemption_type || [],
          deal_summary: item.deal_summary, // Keep full summary for detail view
        }));
        
        const completedCount = transformedData.filter(
          (item) => item.redemption_status === "completed"
        ).length;
        const totalCount = transformedData.length;

        // Calculate rate using direct values (round to 1 decimal to avoid e.g. 66.66666666666666%)
        const completionRate =
          totalCount > 0
            ? Math.round((completedCount / totalCount) * 1000) / 10
            : 0;

        setRedemptionData(transformedData);
        setTotalRedemptions(totalCount);
        setTotalPendingRedemptions(
          transformedData.filter((item) => item.redemption_status === "redeemed").length
        );
        setTotalCompletedRedemptions(completedCount);
        setTotalRedemptionCompletionRate(completionRate);
      } else {
        toast.error(response?.data?.message);
      }
    } catch (error) {
      toast.error("Sorry, Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  // Use the function in useEffect
  useEffect(() => {
    fetchRedemptions();
  }, []);

  // Permission-aware statistics data
  const getStatsData = () => {
    const baseStats = [
      {
        title: "Total Redemptions",
        value: totalRedemptions,
        icon: Tag,
        change: "+12.5%",
        trend: "up",
        bgGradient: "from-rose-500/10 to-orange-500/10",
        iconColor: "text-rose-500",
        borderGradient: "from-rose-500/20 to-orange-500/20",
      },
    ];

    // Add pending redemptions if user has edit permission
    if (hasRedeemEditPermission) {
      baseStats.push({
        title: "Pending Redemptions",
        value: totalPendingRedemptions,
        icon: Users,
        change: "+5.2%",
        trend: "up",
        bgGradient: "from-blue-500/10 to-cyan-500/10",
        iconColor: "text-blue-500",
        borderGradient: "from-blue-500/20 to-cyan-500/20",
      });
    }

    // Add completion rate if user has edit permission
    if (hasRedeemEditPermission) {
      baseStats.push({
        title: "Redemption Completion Rate",
        value: `${Number(totalRedemptionCompletionRate).toFixed(1)}%`,
        icon: BarChart3,
        change: "+3.1%",
        trend: "up",
        bgGradient: "from-green-500/10 to-emerald-500/10",
        iconColor: "text-green-500",
        borderGradient: "from-green-500/20 to-emerald-500/20",
      });
    }

    return baseStats;
  };

  const statsData = getStatsData();
  const uniqueDeals = React.useMemo(() => {
    const deals = redemptionData.map((item) => item.deal_title);
    return ["all", ...new Set(deals)];
  }, [redemptionData]);

  const debouncedSearch = React.useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 300),
    []
  );

  const filteredAndSortedData = React.useMemo(() => {
    let filteredItems = redemptionData;

    // Apply deal filter
    if (selectedDeal !== "all") {
      filteredItems = filteredItems.filter(
        (item) => item.deal_title === selectedDeal
      );
    }

    if (searchTerm.trim()) {
      const searchTerms = searchTerm.toLowerCase().trim().split(" ");
      filteredItems = filteredItems.filter((item) => {
        const         searchableFields = {
          customer: String(item.customer_name || "").toLowerCase(),
          deal: String(item.deal_title || "").toLowerCase(),
          type: String(item.redemption_type || "").toLowerCase(),
          code: String(item.redemption_code || "").toLowerCase(),
          status: String(item.redemption_status || "").toLowerCase(),
          quantity: String(item.quantity || "").toLowerCase(),
          // Unlimited: search "unlimited" or "∞" matches null deal_quantity_left
          dealQtyLeft: (item.deal_quantity_left != null && item.deal_quantity_left !== "")
            ? String(item.deal_quantity_left).toLowerCase()
            : "unlimited ∞",
        };
        return searchTerms.every((term) =>
          Object.values(searchableFields).some((field) => field.includes(term))
        );
      });
    }

    // Apply sorting with type checking
    if (sortConfig.key !== null) {
      filteredItems.sort((a, b) => {
        const keyMap = {
          customerName: "customer_name",
          dealTitle: "deal_title",
          code: "redemption_code",
          quantityRedeemed: "quantity",
          dealQuantityLeft: "deal_quantity_left",
          type: "redemption_type",
          status: "redemption_status",
          date: "updated_at",
        };

        const actualKey = keyMap[sortConfig.key] || sortConfig.key;
        const isNumeric = actualKey === "quantity" || actualKey === "deal_quantity_left";

        const valueA = a[actualKey];
        const valueB = b[actualKey];

        if (isNumeric) {
          // deal_quantity_left: null/empty = unlimited (∞), sort last in asc / first in desc
          const isDealQtyLeft = actualKey === "deal_quantity_left";
          const parseNum = (v) => {
            if (v == null || v === "") return isDealQtyLeft ? Infinity : NaN;
            const n = parseInt(String(v), 10);
            return isNaN(n) && isDealQtyLeft ? Infinity : (isNaN(n) ? NaN : n);
          };
          const numA = parseNum(valueA);
          const numB = parseNum(valueB);
          const comp = (Number.isNaN(numA) ? -1 : numA) - (Number.isNaN(numB) ? -1 : numB);
          return sortConfig.direction === "asc" ? comp : -comp;
        }

        const strA = String(valueA || "").toLowerCase();
        const strB = String(valueB || "").toLowerCase();
        if (strA < strB) return sortConfig.direction === "asc" ? -1 : 1;
        if (strA > strB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filteredItems;
  }, [redemptionData, searchTerm, sortConfig, selectedDeal]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAndSortedData.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);

  // Generate page numbers
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Updated TypeBadge component with safety checks
  const TypeBadge = ({ type }) => {
    // Safety check for undefined or non-array types
    if (!type || !Array.isArray(type) || type.length === 0) {
      return (
        <div className="flex flex-col gap-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700 ring-1 ring-gray-600/20">
            <AlertTriangle className="w-3.5 h-3.5" />
            No Type Specified
          </div>
          <div className="text-[10px] font-medium text-gray-500 flex items-center gap-1">
            <div className="w-1.5 h-1.5 bg-gray-500 rounded-full"></div>
            Redemption type not specified
          </div>
        </div>
      );
    }

    const isBoth = type.includes("Online") && type.includes("In-store");
    const isInStore = type.includes("In-store");

    return (
      <div className="flex flex-col gap-2">
        <div
          className={`
          inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium
          ${
            isBoth
              ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-600/20"
              : isInStore
              ? "bg-purple-50 text-purple-700 ring-1 ring-purple-600/20"
              : "bg-blue-50 text-blue-700 ring-1 ring-blue-600/20"
          }
        `}
        >
          {isBoth ? (
            <>
              <Store className="w-3.5 h-3.5" />
              <Globe className="w-3.5 h-3.5" />
            </>
          ) : isInStore ? (
            <Store className="w-3.5 h-3.5" />
          ) : (
            <Globe className="w-3.5 h-3.5" />
          )}
          {type.length === 1 ? type[0] : "Both (Online & In-store)"}
        </div>
        <div className="text-[10px] font-medium text-gray-500 flex items-center gap-1">
          {isBoth ? (
            <>
              <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></div>
              Available both online & in physical stores
            </>
          ) : isInStore ? (
            <>
              <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
              Available at physical stores
            </>
          ) : (
            <>
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
              Redeemable online only
            </>
          )}
        </div>
      </div>
    );
  };

  // Table headers configuration
  const tableHeaders = [
    { key: "customerName", label: "Customer Name" },
    { key: "dealTitle", label: "Deal Title" },
    { key: "code", label: "Code" },
    { key: "quantityRedeemed", label: "Qty Redeemed" },
    { key: "dealQuantityLeft", label: "Deal Qty Left" },
    { key: "type", label: "Type" },
    { key: "date", label: "Date" },
    { key: "status", label: "Status" },
    { key: "action", label: "Action" },
  ];

  // Updated RedemptionCode component
  const RedemptionCode = ({ code }) => {
    return (
      <div className="inline-flex gap-1.5 items-center px-3 py-1.5 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
        <span className="text-xs font-semibold tracking-wider text-gray-400 uppercase">
          Code
        </span>
        <span className="font-mono text-sm font-medium text-gray-700">
          {code}
        </span>
      </div>
    );
  };

  // Group deals by category
  const groupedDeals = React.useMemo(() => {
    const groups = {};
    redemptionData.forEach((item) => {
      if (!groups[item.category]) {
        groups[item.category] = new Set();
      }
      groups[item.category].add(item.dealTitle);
    });
    return groups;
  }, [redemptionData]);

  // Filter deals based on search term
  const filteredGroupedDeals = React.useMemo(() => {
    if (!filterSearchTerm) return groupedDeals;

    const filtered = {};
    const searchTerm = filterSearchTerm.toLowerCase();

    Object.entries(groupedDeals).forEach(([category, deals]) => {
      const filteredDeals = Array.from(deals).filter(
        (deal) =>
          deal.toLowerCase().includes(searchTerm) ||
          category.toLowerCase().includes(searchTerm)
      );

      if (filteredDeals.length > 0) {
        filtered[category] = new Set(filteredDeals);
      }
    });

    return filtered;
  }, [groupedDeals, filterSearchTerm]);

  // Updated FilterSection component with animations
  const FilterSection = () => (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mb-6 bg-white rounded-lg shadow"
    >
      <div className="p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex justify-between items-center mb-4"
        >
          <div className="flex gap-2 items-center">
            <motion.div
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
            >
              <Filter className="w-4 h-4 text-gray-500" />
            </motion.div>
            <h3 className="text-sm font-medium text-gray-700">
              Filter by Deal
            </h3>
          </div>
          {selectedDeal !== "all" && (
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              onClick={() => setSelectedDeal("all")}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear filter
            </motion.button>
          )}
        </motion.div>

        <div className="relative" ref={dropdownRef}>
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={handleFilterDropdownClick}
            className="w-full flex items-center justify-between px-4 py-2.5 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20"
          >
            <div className="flex gap-2 items-center">
              {selectedDeal === "all" ? (
                "All Deals"
              ) : (
                <>
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-2 h-2 rounded-full bg-[#FF6C6C]"
                  />
                  <span className="truncate max-w-[300px]">{selectedDeal}</span>
                </>
              )}
            </div>
            <motion.div
              animate={{ rotate: isFilterDropdownOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </motion.div>
          </motion.button>

          <AnimatePresence>
            {isFilterDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute z-10 mt-2 w-full bg-white rounded-lg border border-gray-200 shadow-lg"
              >
                <div className="max-h-[400px] overflow-y-auto p-2">
                  <motion.button
                    whileHover={{
                      backgroundColor:
                        selectedDeal === "all" ? "#ff8f8f" : "#f3f4f6",
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setSelectedDeal("all");
                      setIsFilterDropdownOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md ${
                      selectedDeal === "all"
                        ? "bg-[#FF6C6C] text-white"
                        : "text-gray-700"
                    }`}
                  >
                    All Deals
                  </motion.button>

                  {uniqueDeals
                    .filter((deal) => deal !== "all")
                    .map((deal, index) => (
                      <motion.button
                        key={deal}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{
                          backgroundColor:
                            selectedDeal === deal ? "#ff8f8f" : "#f3f4f6",
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setSelectedDeal(deal);
                          setIsFilterDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md flex items-center gap-2 ${
                          selectedDeal === deal
                            ? "bg-[#FF6C6C] text-white"
                            : "text-gray-700"
                        }`}
                      >
                        <motion.span
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: index * 0.05 + 0.1 }}
                          className="w-1.5 h-1.5 bg-current rounded-full opacity-60"
                        />
                        {deal}
                      </motion.button>
                    ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {selectedDeal !== "all" && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-4"
            >
              <div className="flex gap-2 items-center">
                <span className="text-xs text-gray-500">Active filter:</span>
                <motion.span
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-[#FF6C6C]/10 text-[#FF6C6C]"
                >
                  {selectedDeal}
                </motion.span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );

  const handleAction = (action, row) => {
    switch (action) {
      case "redeem":
        setModalData(row);
        setIsModalOpen(true);
        break;
      case "remove":
        console.log("Remove:", row);
        break;
      case "viewDetails":
        setSelectedRedemptionForDetails(row);
        setIsDealDetailsSliderOpen(true);
        break;
      default:
        break;
    }
  };

  const handleModalAction = (actionType, rowData) => {
    console.log("handleModalAction called with:", actionType, rowData);
    setSelectedItem(null); // Reset first
    setTimeout(() => {
      setSelectedItem(rowData);
      setActiveModal(actionType);
    }, 0);
  };

  const calculateDropupPosition = (itemId) => {
    const tableRect = document.querySelector("table").getBoundingClientRect();
    const buttonRect = document
      .querySelector(`#action-${itemId}`)
      ?.getBoundingClientRect();

    if (!buttonRect) return {};

    const distanceFromTop = buttonRect.top - tableRect.top;
    const shouldDropDown = distanceFromTop < 200; // Threshold for switching to dropdown

    return {
      style: shouldDropDown
        ? {
            top: "100%",
            bottom: "auto",
            marginTop: "0.5rem",
            maxHeight: `${window.innerHeight - buttonRect.bottom - 20}px`,
          }
        : {
            bottom: "100%",
            top: "auto",
            marginBottom: "0.5rem",
            maxHeight: `${buttonRect.top - 20}px`,
          },
    };
  };

  const handleFilterDropdownClick = (e) => {
    e.stopPropagation();
    setIsFilterDropdownOpen(!isFilterDropdownOpen);
  };

  const RedemptionModal = ({ isOpen, onClose, data }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const requestedQty = data?.quantity != null && data?.quantity !== ""
      ? parseInt(String(data.quantity), 10)
      : 1;
    const dealQtyLeftRaw = data?.deal_quantity_left;
    // Align with NewDealForm/QuantitySection: null, "", "unlimited" = unlimited
    const unlimitedRaw = dealQtyLeftRaw == null || dealQtyLeftRaw === ""
      || String(dealQtyLeftRaw).trim().toLowerCase() === "unlimited";
    const parsed = !unlimitedRaw ? parseInt(String(dealQtyLeftRaw), 10) : NaN;
    const dealQtyLeft = unlimitedRaw || Number.isNaN(parsed) ? null : parsed;
    const isUnlimited = dealQtyLeft === null;
    const cannotComplete = !isUnlimited && !isNaN(requestedQty) && requestedQty > dealQtyLeft;

    const handleConfirmRedemption = async () => {
      if (cannotComplete) {
        toast.error(
          `Requested quantity (${requestedQty}) exceeds deal quantity left (${dealQtyLeft}). Cannot complete redemption.`
        );
        return;
      }
      setIsSubmitting(true);
      try {
        const response = await DealsRequest.completeRedemption(
          data.user_deal_slug
        );
        if (
          response?.data?.status_code === "000" &&
          !response?.data?.in_error
        ) {
          toast.success("Redemption completed successfully");
          await fetchRedemptions();
          onClose();
        } else {
          toast.error(response?.message || "Failed to complete redemption");
        }
      } catch (error) {
        toast.error("Sorry, something went wrong");
      } finally {
        setIsSubmitting(false);
      }
    };

    if (!isOpen || !data) return null;

    return createPortal(
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 z-50 backdrop-blur-sm bg-black/40"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="flex overflow-auto fixed inset-0 z-50 justify-center items-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative my-8 w-full max-w-lg bg-white rounded-2xl shadow-2xl">
                <div className="sticky top-0 z-10 px-6 py-4 bg-white rounded-t-2xl border-b border-gray-100">
                  <div className="flex gap-3 items-center">
                    <div className="h-8 w-1 bg-gradient-to-b from-[#FF6C6C] to-purple-600 rounded-full" />
                    <h3 className="text-xl font-semibold text-gray-800">
                      Mark as Redeemed
                    </h3>
                  </div>
                  <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-1.5 rounded-full transition-colors hover:bg-gray-100"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="px-6 py-4 max-h-[calc(100vh-16rem)] overflow-y-auto">
                  <div className="space-y-6">
                    <div
                      className={`inline-flex px-3 py-1 text-sm font-medium  rounded-full border  ${
                        data.redemption_status === "completed"
                          ? "text-green-700 bg-green-50 border-green-100"
                          : "border-yellow-100 text-yellow-700 bg-yellow-50"
                      }`}
                    >
                      {data.redemption_status === "completed"
                        ? "Completed"
                        : "Pending Redemption"}
                    </div>

                    <div className="grid gap-6">
                      <div className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                        <div className="flex gap-3 items-center mb-1">
                          <User className="w-4 h-4 text-[#FF6C6C]" />
                          <span className="text-sm font-medium text-gray-700">
                            Customer
                          </span>
                        </div>
                        <p className="ml-7 font-medium text-gray-900">
                          {data.customer_name}
                        </p>
                      </div>

                      <div className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                        <div className="flex gap-3 items-center mb-1">
                          <Tag className="w-4 h-4 text-purple-600" />
                          <span className="text-sm font-medium text-gray-700">
                            Deal
                          </span>
                        </div>
                        <p className="ml-7 font-medium text-gray-900">
                          {data.deal_title}
                        </p>
                      </div>

                      <div className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                        <div className="flex gap-3 items-center mb-1">
                          <Key className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm font-medium text-gray-700">
                            Redemption Code
                          </span>
                        </div>
                        <p className="ml-7 font-mono font-medium text-gray-900">
                          {data.redemption_code}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                          <div className="flex gap-3 items-center mb-1">
                            <Tag className="w-4 h-4 text-amber-600" />
                            <span className="text-sm font-medium text-gray-700">
                              Qty Redeemed
                            </span>
                          </div>
                          <p className="ml-7 font-medium text-gray-900">
                            {data.quantity != null && data.quantity !== "" ? data.quantity : "1"}
                          </p>
                          <p className="ml-7 text-xs text-gray-500">requested by customer</p>
                        </div>
                        <div className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                          <div className="flex gap-3 items-center mb-1">
                            <BarChart3 className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-gray-700">
                              Deal Qty Left
                            </span>
                          </div>
                          <p className="ml-7 font-medium text-gray-900">
                            {data.deal_quantity_left != null && data.deal_quantity_left !== ""
                              ? data.deal_quantity_left
                              : "∞"}
                          </p>
                          <p className="ml-7 text-xs text-gray-500">remaining for deal</p>
                        </div>
                      </div>

                      {cannotComplete && (
                        <div className="p-4 rounded-xl border border-red-200 bg-red-50 flex gap-2 items-start">
                          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-red-800">
                            <p className="font-medium">Cannot complete this redemption</p>
                            <p className="mt-1 text-red-700">
                              Requested quantity ({requestedQty}) is greater than deal quantity left ({dealQtyLeft}).
                              Fulfill or update stock before marking as redeemed.
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                        <div className="flex gap-3 items-center mb-1">
                          <Calendar className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-gray-700">
                            Date
                          </span>
                        </div>
                        <p className="ml-7 font-medium text-gray-900">
                          {moment(data.updated_at).format(
                            "DD MMM YYYY, hh:mm A"
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 px-6 py-4 bg-white rounded-b-2xl border-t border-gray-100">
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={onClose}
                      className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    {data?.redemption_status !== "completed" && (
                      <button
                        onClick={handleConfirmRedemption}
                        disabled={isSubmitting || cannotComplete}
                        className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] rounded-xl hover:opacity-90 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="mr-2 -ml-1 w-4 h-4 text-white animate-spin"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Processing...
                          </>
                        ) : (
                          "Confirm"
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  };

  const RedemptionTypeModal = ({ isOpen, onClose, item, type }) => {
    const [selectedLocation, setSelectedLocation] = useState("");

    const locations =
      type === "online"
        ? [
            { id: "website", label: "Website" },
            { id: "mobile_app", label: "Mobile App" },
            { id: "marketplace", label: "Marketplace" },
          ]
        : [
            { id: "store_1", label: "Store Location 1" },
            { id: "store_2", label: "Store Location 2" },
            { id: "store_3", label: "Store Location 3" },
          ];

    const handleSubmit = () => {
      console.log(`Setting redemption ${type}:`, { item, selectedLocation });
      onClose();
    };

    if (!isOpen || !item) return null;

    return createPortal(
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 z-50 backdrop-blur-sm bg-black/40"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="flex overflow-auto fixed inset-0 z-50 justify-center items-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative my-8 w-full max-w-lg bg-white rounded-2xl shadow-2xl">
                <div className="sticky top-0 z-10 px-6 py-4 bg-white rounded-t-2xl border-b border-gray-100">
                  <div className="flex gap-3 items-center">
                    <div className="h-8 w-1 bg-gradient-to-b from-[#FF6C6C] to-purple-600 rounded-full" />
                    <h3 className="text-xl font-semibold text-gray-800">
                      {type === "online" ? "Online" : "In-store"} Redemption
                    </h3>
                  </div>
                  <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-1.5 rounded-full transition-colors hover:bg-gray-100"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="px-6 py-4 max-h-[calc(100vh-16rem)] overflow-y-auto">
                  <div className="space-y-6">
                    <div className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                      <div className="space-y-3 text-sm">
                        <div className="flex items-center">
                          <span className="w-24 text-gray-500">Deal</span>
                          <span className="font-medium text-gray-900">
                            {item?.deal_title}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="w-24 text-gray-500">Code</span>
                          <span className="font-mono font-medium text-gray-900">
                            {item?.redemption_code}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Select{" "}
                        {type === "online" ? "Platform" : "Store Location"}
                      </label>
                      <select
                        value={selectedLocation}
                        onChange={(e) => setSelectedLocation(e.target.value)}
                        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-colors"
                      >
                        <option value="">Select an option</option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 px-6 py-4 bg-white rounded-b-2xl border-t border-gray-100">
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={onClose}
                      className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={!selectedLocation}
                      className="px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] rounded-xl hover:opacity-90 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  };

  const getDropdownPosition = (buttonElement) => {
    if (!buttonElement) return {};

    const rect = buttonElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const showBelow = spaceBelow >= 200 || spaceBelow > spaceAbove;

    // Calculate right alignment
    const menuWidth = 180; // Width of the dropdown menu
    const rightSpace = window.innerWidth - rect.left;
    const shouldAlignLeft = rightSpace < menuWidth;

    return {
      position: "fixed",
      ...(shouldAlignLeft
        ? { right: `${window.innerWidth - rect.right}px` }
        : { left: `${rect.left}px` }),
      ...(showBelow
        ? { top: `${rect.bottom + 8}px` }
        : { bottom: `${window.innerHeight - rect.top + 8}px` }),
    };
  };

  // Add this console log to see what data we're getting
  useEffect(() => {
    console.log("Current Items:", currentItems);
  }, [currentItems]);

  // Add this to track selectedItem changes
  useEffect(() => {
    console.log("Selected Item Changed:", selectedItem);
  }, [selectedItem]);

  // Debug: Log permission information
  console.log("🔍 Permission Debug Info:", {
    userPermissions,
    isMainVendor,
    hasRedeemOffersPermission,
    hasRedeemEditPermission,
    hasRedeemDeletePermission,
    redemptionPermissions: userPermissions?.["Redeem_Offers"],
  });

  // Check if user has any redemption permissions
  const hasAnyRedemptionPermission =
    hasRedeemOffersPermission ||
    hasRedeemEditPermission ||
    hasRedeemDeletePermission;

  // Permission check - if no redemption permissions, show modal
  if (!hasAnyRedemptionPermission) {
    console.log(
      "❌ No redemption permissions found - showing PermissionDeniedModal"
    );
    return (
      <PermissionDeniedModal message="You do not have permission to access redemption management." />
    );
  }

  return (
    <div>
      <Navbar page={"Redemptions"} />
      <div className="p-6 min-h-screen bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="overflow-hidden relative mb-8"
        >
          <div className="flex relative flex-col p-4 sm:p-6 md:p-8 bg-white rounded-2xl shadow-sm">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#FF6C6C]/10 to-purple-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr rounded-full blur-2xl from-blue-500/10 to-yellow-500/10"></div>

            <div className="relative z-10 w-full">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                <div className="flex gap-3 items-center">
                  <div className="h-6 sm:h-8 w-1 bg-gradient-to-b from-[#FF6C6C] to-purple-600 rounded-full flex-shrink-0"></div>
                  <motion.h1
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-lg sm:text-xl md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700"
                  >
                    Redemption Overview
                  </motion.h1>
                </div>
                {/* Permission Badge */}
                <div className="flex gap-2 items-center ml-0 sm:ml-2">
                  <div className="flex gap-1 items-center px-2 sm:px-3 py-1 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-full border border-blue-200">
                    <Shield className="w-3 h-3 text-blue-600 flex-shrink-0" />
                    <span className="text-[10px] sm:text-xs font-medium text-blue-700 whitespace-nowrap">
                      {hasRedeemOffersPermission ? "View" : ""}
                      {hasRedeemEditPermission ? " + Edit" : ""}
                      {hasRedeemDeletePermission ? " + Delete" : ""}
                    </span>
                  </div>
                </div>
              </div>
              <p className="mt-2 text-xs sm:text-sm text-gray-600 max-w-full md:max-w-xl">
                Track and manage customer redemptions with real-time insights
                and detailed analytics
              </p>

              {/* Permission-aware description */}
              <div className="flex gap-2 items-center mt-3 overflow-x-auto">
                <div className="flex flex-wrap gap-2 sm:gap-4 items-center text-xs text-gray-500">
                  {hasRedeemOffersPermission && (
                    <div className="flex gap-1 items-center whitespace-nowrap">
                      <Eye className="w-3 h-3 text-green-500 flex-shrink-0" />
                      <span className="text-[10px] sm:text-xs">View redemptions</span>
                    </div>
                  )}
                  {hasRedeemEditPermission && (
                    <div className="flex gap-1 items-center whitespace-nowrap">
                      <Edit3 className="w-3 h-3 text-blue-500 flex-shrink-0" />
                      <span className="text-[10px] sm:text-xs">Mark as redeemed</span>
                    </div>
                  )}
                  {hasRedeemDeletePermission && (
                    <div className="flex gap-1 items-center whitespace-nowrap">
                      <Trash2 className="w-3 h-3 text-red-500 flex-shrink-0" />
                      <span className="text-[10px] sm:text-xs">Remove entries</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 gap-6 mt-8 md:grid-cols-3"
          >
            {statsData.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className={`
                  relative overflow-hidden p-6 bg-white rounded-2xl
                  before:absolute before:inset-0 before:bg-gradient-to-br ${stat.bgGradient}
                  after:absolute after:inset-px after:rounded-[15px] after:bg-gradient-to-br ${stat.borderGradient}
                  shadow-sm hover:shadow-md transition-all duration-300
                `}
              >
                <div className="relative z-10">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        {stat.title}
                      </p>
                      <h3 className="mt-2 text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700">
                        {stat.value}
                      </h3>
                    </div>
                    <div
                      className={`
                      p-3 rounded-xl bg-gradient-to-br ${stat.bgGradient}
                      border border-gradient-to-br ${stat.borderGradient}
                    `}
                    >
                      <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
                    </div>
                  </div>

                  <div className="absolute right-0 bottom-0 opacity-10">
                    <stat.icon className="w-24 h-24" />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        <FilterSection />

        {/* Permission-aware Bulk Action Controls */}
        {isBulkActionMode &&
          (hasRedeemEditPermission || hasRedeemDeletePermission) && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 mb-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200"
            >
              <div className="flex gap-4 justify-between items-center">
                <div className="flex gap-4 items-center">
                  <div className="flex gap-2 items-center">
                    <Settings className="w-5 h-5 text-purple-600" />
                    <span className="text-sm font-medium text-purple-700">
                      Bulk Actions Mode
                    </span>
                  </div>
                  <span className="text-sm text-purple-600">
                    {selectedItems.length} item
                    {selectedItems.length !== 1 ? "s" : ""} selected
                  </span>
                </div>

                <div className="flex gap-2 items-center">
                  {hasRedeemEditPermission && (
                    <button
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700"
                      onClick={() => {
                        if (selectedItems.length > 0) {
                          toast.info(
                            `Marking ${selectedItems.length} items as redeemed...`
                          );
                          // Bulk redeem functionality would go here
                        } else {
                          toast.warning("Please select items first");
                        }
                      }}
                    >
                      <CheckCircle2 className="mr-2 w-4 h-4" />
                      Mark Selected as Redeemed
                    </button>
                  )}

                  {hasRedeemDeletePermission && (
                    <button
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700"
                      onClick={() => {
                        if (selectedItems.length > 0) {
                          toast.info(
                            `Removing ${selectedItems.length} items...`
                          );
                          // Bulk delete functionality would go here
                        } else {
                          toast.warning("Please select items first");
                        }
                      }}
                    >
                      <Trash2 className="mr-2 w-4 h-4" />
                      Remove Selected
                    </button>
                  )}

                  <button
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                    onClick={() => {
                      setSelectedItems([]);
                      setIsBulkActionMode(false);
                    }}
                  >
                    <X className="mr-2 w-4 h-4" />
                    Clear Selection
                  </button>
                </div>
              </div>
            </motion.div>
          )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="p-4 mb-4 bg-white rounded-lg shadow"
        >
          <div className="relative w-full max-w-md">
            <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
              <Search className="w-4 h-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-colors"
              placeholder="Search by customer, deal title, code or status..."
              defaultValue={searchTerm}
              onChange={(e) => debouncedSearch(e.target.value)}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="overflow-x-auto bg-white rounded-lg shadow"
        >
          <table className="w-full whitespace-nowrap">
            <thead className="bg-gray-50">
              <tr>
                {/* Bulk Selection Checkbox */}
                {isBulkActionMode &&
                  (hasRedeemEditPermission || hasRedeemDeletePermission) && (
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-[#FF6C6C] bg-gray-100 border-gray-300 rounded focus:ring-[#FF6C6C] focus:ring-2"
                        checked={
                          selectedItems.length === currentItems.length &&
                          currentItems.length > 0
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems(
                              currentItems.map((item) => item.redemption_id)
                            );
                          } else {
                            setSelectedItems([]);
                          }
                        }}
                      />
                    </th>
                  )}

                {tableHeaders.map((header) => (
                  <th
                    key={header.key}
                    onClick={() =>
                      header.key !== "action" && handleSort(header.key)
                    }
                    className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex gap-2 items-center whitespace-nowrap">
                      {header.label}
                      {header.key !== "action" && (
                        <ArrowUpDown
                          className={`w-4 h-4 flex-shrink-0 transition-transform ${
                            sortConfig.key === header.key
                              ? sortConfig.direction === "asc"
                                ? "text-[#FF6C6C]"
                                : "text-[#FF6C6C] rotate-180"
                              : ""
                          }`}
                        />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={isBulkActionMode ? 10 : 9}
                    className="px-6 py-12 text-center"
                  >
                    <div className="flex flex-col gap-4 items-center">
                      <div className="p-4 bg-gray-100 rounded-full">
                        {hasRedeemOffersPermission ? (
                          <FileText className="w-8 h-8 text-gray-400" />
                        ) : (
                          <Lock className="w-8 h-8 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="mb-2 text-lg font-medium text-gray-900">
                          {hasRedeemOffersPermission
                            ? "No redemptions found"
                            : "Access restricted"}
                        </h3>
                        <p className="max-w-md text-sm text-gray-500">
                          {hasRedeemOffersPermission
                            ? "There are no redemption records matching your current filters. Try adjusting your search criteria or check back later."
                            : "You don't have permission to view redemption data. Contact your administrator to request access."}
                        </p>
                      </div>
                      {hasRedeemOffersPermission && (
                        <button
                          onClick={() => {
                            setSearchTerm("");
                            setSelectedDeal("all");
                            setCurrentPage(1);
                          }}
                          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-[#FF6C6C] rounded-lg hover:bg-[#FF8F8F] transition-colors"
                        >
                          <RefreshCw className="mr-2 w-4 h-4" />
                          Clear Filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                currentItems.map((rowItem, index) => (
                  <tr
                    key={rowItem.redemption_id || index}
                    className={`transition-colors hover:bg-gray-50/50 ${
                      selectedItems.includes(rowItem.redemption_id)
                        ? "bg-purple-50"
                        : ""
                    }`}
                  >
                    {/* Bulk Selection Checkbox */}
                    {isBulkActionMode &&
                      (hasRedeemEditPermission ||
                        hasRedeemDeletePermission) && (
                        <td className="px-6 py-3">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-[#FF6C6C] bg-gray-100 border-gray-300 rounded focus:ring-[#FF6C6C] focus:ring-2"
                            checked={selectedItems.includes(
                              rowItem.redemption_id
                            )}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedItems([
                                  ...selectedItems,
                                  rowItem.redemption_id,
                                ]);
                              } else {
                                setSelectedItems(
                                  selectedItems.filter(
                                    (id) => id !== rowItem.redemption_id
                                  )
                                );
                              }
                            }}
                          />
                        </td>
                      )}
                    <td className="px-6 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {rowItem.customer_name}
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      <div className="max-w-xs text-sm text-gray-600 truncate">
                        {rowItem.deal_title}
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      <RedemptionCode code={rowItem.redemption_code} />
                    </td>
                    <td className="px-6 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {rowItem.quantity != null && rowItem.quantity !== ""
                          ? rowItem.quantity
                          : "—"}
                      </div>
                      <div className="text-[10px] text-gray-500">requested</div>
                    </td>
                    <td className="px-6 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {rowItem.deal_quantity_left != null && rowItem.deal_quantity_left !== ""
                          ? rowItem.deal_quantity_left
                          : "∞"}
                      </div>
                      <div className="text-[10px] text-gray-500">left</div>
                    </td>
                    <td className="px-6 py-3">
                      <TypeBadge type={rowItem.redemption_type} />
                    </td>
                    <td className="px-6 py-3">
                      <div className="text-sm text-gray-600">
                        {moment(rowItem.updated_at).format("DD-MMM-YYYY")}
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      <span
                        className={`
                      px-2.5 py-1 text-xs font-medium rounded-full
                      ${
                        rowItem.redemption_status === "completed"
                          ? "bg-green-50 text-green-700 ring-1 ring-green-600/20"
                          : rowItem.redemption_status === "redeemed"
                          ? "bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20"
                          : "bg-blue-50 text-blue-700 ring-1 ring-blue-600/20"
                      }
                    `}
                      >
                        {rowItem.redemption_status === "redeemed"
                          ? "Pending Redemption"
                          : rowItem.redemption_status === "completed"
                          ? "Redeemed"
                          : "In Progress"}
                      </span>
                    </td>
                    <td className="relative px-6 py-3">
                      <ActionMenu row={rowItem} onAction={handleAction} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="flex justify-between items-center px-6 py-4 bg-white border-t border-gray-200">
            <div className="flex items-center text-sm text-gray-500">
              <span>
                Showing {indexOfFirstItem + 1} to{" "}
                {Math.min(indexOfLastItem, filteredAndSortedData.length)} of{" "}
                {filteredAndSortedData.length} results
              </span>
            </div>

            <div className="flex gap-2 items-center">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`
                  inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg
                  ${
                    currentPage === 1
                      ? "text-gray-400 bg-gray-50 cursor-not-allowed"
                      : "text-gray-700 bg-white hover:bg-gray-50 border border-gray-200"
                  }
                `}
              >
                Previous
              </button>

              <div className="flex gap-1 items-center">
                {pageNumbers.map((number) => (
                  <button
                    key={number}
                    onClick={() => setCurrentPage(number)}
                    className={`
                      inline-flex items-center justify-center w-8 h-8 text-sm font-medium rounded-lg
                      ${
                        currentPage === number
                          ? "text-white bg-[#FF6C6C]"
                          : "text-gray-700 bg-white hover:bg-gray-50 border border-gray-200"
                      }
                    `}
                  >
                    {number}
                  </button>
                ))}
              </div>

              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className={`
                  inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg
                  ${
                    currentPage === totalPages
                      ? "text-gray-400 bg-gray-50 cursor-not-allowed"
                      : "text-gray-700 bg-white hover:bg-gray-50 border border-gray-200"
                  }
                `}
              >
                Next
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      <RedemptionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setModalData(null);
        }}
        data={modalData}
      />

      <RedemptionTypeModal
        isOpen={activeModal === "online"}
        onClose={() => {
          setActiveModal(null);
          setSelectedItem(null);
        }}
        item={selectedItem}
        type="online"
      />

      <RedemptionTypeModal
        isOpen={activeModal === "in-store"}
        onClose={() => {
          setActiveModal(null);
          setSelectedItem(null);
        }}
        item={selectedItem}
        type="in-store"
      />

      <RedemptionDealDetailsSlider
        isOpen={isDealDetailsSliderOpen}
        onClose={() => {
          setIsDealDetailsSliderOpen(false);
          setSelectedRedemptionForDetails(null);
        }}
        redemptionData={selectedRedemptionForDetails}
      />
    </div>
  );
};

const ActionMenu = ({ row, onAction }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    vertical: 'bottom',
    horizontal: 'right',
    style: {}
  });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  // Permission checks (passed from parent component)
  const userPermissions = useSelector((state) => state?.user?.userPermissions);
  const isMainVendor = useSelector((state) => state?.user?.isMainVendor);

  const hasRedeemEditPermission =
    isMainVendor ||
    (userPermissions &&
      userPermissions["Redeem_Offers"] &&
      userPermissions["Redeem_Offers"].includes("Redeem_Edit"));

  const hasRedeemDeletePermission =
    isMainVendor ||
    (userPermissions &&
      userPermissions["Redeem_Offers"] &&
      userPermissions["Redeem_Offers"].includes("Redeem_Delete"));

  // Smart positioning logic - calculates optimal dropdown position using FIXED positioning
  // Matches the pattern used in Offers.js for consistency
  const calculateDropdownPosition = () => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownHeight = 280; // Estimated dropdown height
    const dropdownWidth = 256; // w-64 = 16rem = 256px
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Calculate available space
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const spaceRight = viewportWidth - buttonRect.right;
    const spaceLeft = buttonRect.left;

    // Build style object
    const style = {
      position: 'fixed',
      zIndex: 9999,
    };
    
    // Determine vertical position (smart!)
    let vertical = 'bottom';
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      // Not enough space below and more space above - show above
      vertical = 'top';
      style.bottom = `${viewportHeight - buttonRect.top + 8}px`;
    } else {
      // Default: show below
      vertical = 'bottom';
      style.top = `${buttonRect.bottom + 8}px`;
    }

    // Determine horizontal position - render dropdown to the left of the button
    let horizontal = 'right';
    
    // Default: align dropdown's right edge with the button's right edge
    // This makes the dropdown appear to the left of the button
    style.right = `${viewportWidth - buttonRect.right}px`;
    
    // If not enough space on the left, align to the right side instead
    if (spaceLeft < dropdownWidth && spaceRight > spaceLeft) {
      horizontal = 'left';
      style.right = 'auto';
      style.left = `${buttonRect.left}px`;
    }

    setDropdownPosition({
      vertical,
      horizontal,
      style
    });
  };

  // Recalculate position when dropdown opens
  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
      
      // Recalculate on scroll or resize
      const handleReposition = () => {
        if (isOpen) {
          calculateDropdownPosition();
        }
      };

      window.addEventListener('scroll', handleReposition, true);
      window.addEventListener('resize', handleReposition);
      
      return () => {
        window.removeEventListener('scroll', handleReposition, true);
        window.removeEventListener('resize', handleReposition);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both button and dropdown
      const isClickOutsideButton = buttonRef.current && !buttonRef.current.contains(event.target);
      const isClickOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);
      
      if (isOpen && isClickOutsideButton && isClickOutsideDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Build actions array based on permissions
  const actions = [];

  // View Deal Details - always available (view permission already checked at parent level)
  actions.push({
    icon: <Eye size={16} className="text-blue-600" />,
    label: "View Deal Details",
    action: "viewDetails",
    permission: "View",
    available: true,
    description: "View complete deal information",
  });

  // Mark as Redeemed action - requires Redeem_Edit permission
  if (hasRedeemEditPermission && row.redemption_status !== "completed") {
    actions.push({
      icon: <CheckCircle2 size={16} className="text-green-600" />,
      label: "Mark as Redeemed",
      action: "redeem",
      permission: "Redeem_Edit",
      available: true,
      description: "Mark this redemption as completed",
    });
  }

  // Remove Entry action - requires Redeem_Delete permission
  // if (hasRedeemDeletePermission) {
  //   actions.push({
  //     icon: <Trash2 size={16} className="text-red-600" />,
  //     label: "Remove Entry",
  //     action: "remove",
  //     permission: "Redeem_Delete",
  //     available: true,
  //     description: "Permanently remove entry",
  //   });
  // }

  // If no actions available, show disabled state
  if (actions.length === 0) {
    return (
      <>
        <div className="relative" ref={menuRef}>
          <button
            ref={buttonRef}
            className="relative p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            title="No actions available"
          >
            <MoreVertical size={16} className="text-gray-400" />
            <Lock size={12} className="absolute -top-1 -right-1 text-gray-400" />
          </button>
        </div>

        {isOpen && createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: dropdownPosition.vertical === 'top' ? 10 : -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: dropdownPosition.vertical === 'top' ? 10 : -10 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="w-56 sm:w-64 bg-white rounded-lg border border-gray-200 shadow-2xl"
              style={dropdownPosition.style}
              ref={dropdownRef}
            >
              <div className="px-3 py-2 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex gap-2 items-center">
                  <Shield className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-xs sm:text-sm font-medium text-gray-600">
                    No Actions Available
                  </span>
                </div>
              </div>
              <div className="px-3 py-2">
                <p className="text-[10px] sm:text-xs text-gray-500 leading-relaxed">
                  You don't have permission to perform actions on redemptions.
                  Contact your administrator for access.
                </p>
              </div>
              
              {/* Smart position indicator (subtle visual feedback) */}
              <div className="absolute w-2 h-2 bg-white border-gray-200 transform rotate-45 pointer-events-none"
                style={{
                  [dropdownPosition.vertical === 'top' ? 'bottom' : 'top']: '-4px',
                  [dropdownPosition.horizontal]: '12px',
                  borderWidth: dropdownPosition.vertical === 'top' ? '0 1px 1px 0' : '1px 0 0 1px'
                }}
              />
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
      </>
    );
  }

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button
          ref={buttonRef}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-all hover:shadow-sm active:scale-95"
          onClick={() => setIsOpen(!isOpen)}
        >
          <MoreVertical size={16} className="text-gray-500" />
        </button>
      </div>

      {isOpen && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ 
              opacity: 0, 
              scale: 0.95, 
              y: dropdownPosition.vertical === 'top' ? 10 : -10 
            }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ 
              opacity: 0, 
              scale: 0.95, 
              y: dropdownPosition.vertical === 'top' ? 10 : -10 
            }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="w-52 sm:w-64 bg-white rounded-lg border border-gray-200 shadow-2xl max-w-[calc(100vw-2rem)]"
            style={dropdownPosition.style}
            ref={dropdownRef}
          >
            {/* Header */}
            <div className="px-3 py-2 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
              <div className="flex gap-2 items-center">
                <Settings className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-medium text-gray-700 truncate">
                  Redemption Actions
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="py-1 max-h-[60vh] overflow-y-auto">
              {actions.map((action, index) => (
                <motion.button
                  key={action.action}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex gap-2 items-start px-3 py-2.5 w-full text-sm text-left transition-all hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 group"
                  onClick={() => {
                    onAction(action.action, row);
                    setIsOpen(false);
                  }}
                >
                  <div className="mt-0.5 flex-shrink-0 transition-transform group-hover:scale-110">
                    {action.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-700 text-xs sm:text-sm break-words group-hover:text-gray-900">
                      {action.label}
                    </div>
                    <div className="text-[10px] sm:text-xs text-gray-500 break-words leading-relaxed mt-0.5">
                      {action.description}
                    </div>
                    <div className="mt-1.5">
                      <span className="inline-block px-2 py-0.5 text-[10px] text-blue-700 bg-blue-50 group-hover:bg-blue-100 rounded-full transition-colors border border-blue-200">
                        {action.permission}
                      </span>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Footer with permission info */}
            <div className="px-3 py-2 bg-gradient-to-r from-gray-50 to-white border-t border-gray-100">
              <div className="flex gap-2 justify-between items-center">
                <div className="flex gap-2 items-center">
                  <Shield className="w-3 h-3 text-gray-400 flex-shrink-0" />
                  <span className="text-[10px] sm:text-xs text-gray-500">
                    {actions.length} action{actions.length !== 1 ? "s" : ""} available
                  </span>
                </div>
                {/* Position indicator badge */}
                <div className="flex gap-1 items-center px-1.5 py-0.5 bg-white rounded-full border border-gray-200">
                  {dropdownPosition.vertical === 'top' ? (
                    <ChevronDown className="w-3 h-3 text-gray-400 rotate-180" />
                  ) : (
                    <ChevronDown className="w-3 h-3 text-gray-400" />
                  )}
                </div>
              </div>
            </div>
            
            {/* Smart position pointer/arrow */}
            <div 
              className="absolute w-2 h-2 bg-white border-gray-200 transform rotate-45 pointer-events-none"
              style={{
                [dropdownPosition.vertical === 'top' ? 'bottom' : 'top']: '-4px',
                [dropdownPosition.horizontal]: '12px',
                borderWidth: dropdownPosition.vertical === 'top' ? '0 1px 1px 0' : '1px 0 0 1px'
              }}
            />
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default RedemptionOverview;
